/**
 * Показ чисел ПРЦ: деньги «11 991,65 ₽», проценты, часы. Всё — на строках через
 * `formatDecimal` справочника техники: деньги через float не проходят.
 */
import { formatBytes, pluralizeRu } from '@shared/lib/formatFile';
import { NBSP, formatCount, formatDecimal, formatMoney } from '@entities/fleet';
import { decimalSign, roundDecimal, shiftDecimal, toDecimalText } from '@entities/prc';

export const MODELS = ['модель', 'модели', 'моделей'] as const;
export const MODELS_GEN = ['модели', 'моделей', 'моделей'] as const;
export const POSITIONS = ['позиция', 'позиции', 'позиций'] as const;
export const ROWS = ['строка', 'строки', 'строк'] as const;
export const VALUES = ['значение', 'значения', 'значений'] as const;
export const VERSIONS = ['версия', 'версии', 'версий'] as const;
export const PROFESSIONS = ['профессия', 'профессии', 'профессий'] as const;

/** «141 модель», «3 модели». */
export function count(n: number, forms: readonly [string, string, string]): string {
  return `${formatCount(n)}${NBSP}${pluralizeRu(n, forms)}`;
}

const RUB = '₽';

function plus(text: string, raw: string, signed: boolean): string {
  return signed && decimalSign(raw) === 1 ? `+${text}` : text;
}

/** Деньги: «11 991,65 ₽»; пусто — «—». `signed` — «+141,58 ₽» у роста. */
export function formatRub(raw: unknown, signed = false): string {
  const d = toDecimalText(raw);
  if (d === null) return '—';
  return `${plus(formatMoney(d), d, signed)}${NBSP}${RUB}`;
}

/**
 * Деньги полной точности (`results?model_id`), округлённые как строки моделей
 * на сервере (`money` — половина от нуля, как ROUND в Excel и NUMERIC в
 * Postgres): цифры панели модели совпадают с таблицей и с утверждённой ценой.
 */
export function formatServerMoney(raw: unknown, withSign = true): string {
  const d = toDecimalText(raw);
  if (d === null) return '—';
  const rounded = roundDecimal(d, 2, 'half-up') ?? d;
  return withSign ? formatRub(rounded) : formatMoneyCell(rounded);
}

/** Деньги без знака рубля — для ячеек таблиц, где рубли в заголовке. */
export function formatMoneyCell(raw: unknown, signed = false): string {
  const d = toDecimalText(raw);
  if (d === null) return '—';
  return plus(formatMoney(d), d, signed);
}

/** Число с разрядами: до `max` знаков после запятой. */
export function formatNumber(raw: unknown, max = 2, min = 0, signed = false): string {
  const d = toDecimalText(raw);
  if (d === null) return '—';
  const text = formatDecimal(d, { minFraction: min, maxFraction: max, grouping: true }) ?? d;
  return plus(text, d, signed);
}

/** Проценты как есть: «3.45» → «3,45 %». */
export function formatPercent(raw: unknown, max = 2, signed = false): string {
  const d = toDecimalText(raw);
  if (d === null) return '—';
  return `${formatNumber(d, max, 0, signed)}${NBSP}%`;
}

/** Доля в процентах: «0.4630» → «46,3 %». */
export function formatShare(raw: unknown, max = 1, signed = false): string {
  const d = toDecimalText(raw);
  if (d === null) return '—';
  return formatPercent(shiftDecimal(d, 2) ?? d, max, signed);
}

/** Часы: «172,5 ч». */
export function formatHours(raw: unknown, max = 1): string {
  const d = toDecimalText(raw);
  if (d === null) return '—';
  return `${formatNumber(d, max)}${NBSP}ч`;
}

/** День события по часам пользователя: «20.10.2026». */
export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Размер файла: «5,4 МБ». */
export function formatSize(bytes: number): string {
  return formatBytes(bytes).replace('.', ',');
}

/** Имя человека или «—», если пользователь удалён. */
export function personName(name: string | null | undefined): string {
  return name?.trim() || '—';
}

/** Имя файла без символов, недопустимых в Windows. */
export function safeFileName(s: string): string {
  return s
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Сохранить файл по object URL и сразу освободить его. */
export function saveObjectUrl(url: string, name: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
